import { useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Button, Input, Paragraph, PlusIcon, SearchIcon } from '@quickmd-llc/design-system'
import { useGetPatients } from '@/api/generated/patient/patient'
import { ADMIN_EVENT_NAMES, tracking } from '@/lib/segment/tracking'
import { type Patient } from '@/types/patient'
import { AccountCard } from './AccountCard'
import { useMergeAccounts } from './context/useMergeAccounts'
import { pluralize } from '@quickmd-llc/frontend-utils'

function EmptyState({
  showNoSearchResults,
  showNoRemainingResults,
}: {
  showNoSearchResults: boolean
  showNoRemainingResults: boolean
}) {
  let title = 'Begin searching'
  let description = 'Search for and select accounts that you would like to merge.'

  if (showNoSearchResults) {
    title = 'No results found'
    description = 'We couldn’t find any results. Try adjusting your search to find accounts to merge.'
  } else if (showNoRemainingResults) {
    title = 'No remaining results'
    description = 'All matching accounts have been added to the merge list.'
  }

  return (
    <div className='flex flex-1 items-center justify-center p-sp-2'>
      <div className='flex flex-col items-center gap-sp-1 text-center'>
        <SearchIcon iconSize='xxl' className='text-text-light' />
        <Paragraph className='font-semibold text-text-dark'>{title}</Paragraph>
        <Paragraph>{description}</Paragraph>
      </div>
    </div>
  )
}

export function AvailableAccounts() {
  const { addPatients, searchValue, setSearchValue, filterDob, setFilterDob, selectedPatients } = useMergeAccounts()

  const [inputValue, setInputValue] = useState<string>('')
  const [patientsReadyToMerge, setPatientsReadyToMerge] = useState<Array<Patient>>([])

  const debouncedSetSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_AVAILABLE_ACCOUNTS_SEARCH)

        setSearchValue(value)
      }, 300),
    [setSearchValue],
  )
  const { data, isLoading } = useGetPatients<Array<Patient>>(
    // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
    { SearchValue: searchValue, Count: 50, BirthDate: filterDob || undefined },
    {
      query: {
        enabled: Boolean(searchValue.trim()),
      },
    },
  )

  const availablePatients = useMemo(() => {
    if (!data) return []

    return data.filter((patient) => !selectedPatients.find((p) => p.PatientId === patient.PatientId))
  }, [data, selectedPatients])

  const showNoSearchResults = Boolean(searchValue) && data?.length === 0
  const showNoRemainingResults = Boolean(searchValue) && availablePatients.length === 0 && selectedPatients.length > 0

  useEffect(() => {
    setPatientsReadyToMerge([])
  }, [searchValue, filterDob])

  useEffect(() => {
    setInputValue(searchValue)
  }, [searchValue])

  function handleClickAccountCard(patient: Patient) {
    if (!patientsReadyToMerge.find((p) => p.PatientId === patient.PatientId)) {
      tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_AVAILABLE_ACCOUNTS_SELECT_ACCOUNT)

      setPatientsReadyToMerge((prev) => [...prev, patient])
    } else {
      setPatientsReadyToMerge((prev) => prev.filter((p) => p.PatientId !== patient.PatientId))
    }
  }

  function handleClickAddToMergeList() {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_AVAILABLE_ACCOUNTS_ADD_TO_MERGE_LIST)

    addPatients(patientsReadyToMerge)
    setPatientsReadyToMerge([])
  }

  function handleChangeDate(value: string) {
    tracking.trackEvent(ADMIN_EVENT_NAMES.MERGE_ACCOUNTS_AVAILABLE_ACCOUNTS_FILTER_BY_DATE)

    setFilterDob(value)
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-border-dark bg-white'>
      <div className='grid gap-sp-2 p-sp-2'>
        <div className='flex items-start justify-between'>
          <div>
            <Paragraph className='text-dark font-semibold'>Available accounts</Paragraph>
            <Paragraph>
              {patientsReadyToMerge.length} {pluralize('account', patientsReadyToMerge.length)} selected for merging
            </Paragraph>
          </div>

          <Button
            size='s'
            className='flex-shrink-0'
            disabled={patientsReadyToMerge.length === 0}
            onClick={() => handleClickAddToMergeList()}>
            <PlusIcon />
            Add to merge list
          </Button>
        </div>

        <div className='grid gap-sp-2 [grid-template-columns:2fr_1fr]'>
          <Input
            placeholder='Search by name, phone, email, and MRN'
            onChange={({ target }) => {
              debouncedSetSearchValue(target.value)
              setInputValue(target.value)
            }}
            loading={isLoading}
            value={inputValue}
          />

          <Input type='date' onChange={({ target }) => handleChangeDate(target.value)} />
        </div>
      </div>

      {availablePatients.length > 0 ?
        <div className='overflow-y-auto p-sp-2'>
          <div className='grid gap-sp-2'>
            {availablePatients.map((patient) => (
              <AccountCard
                key={patient.PatientId}
                patient={patient}
                onClick={(p) => handleClickAccountCard(p)}
                isSelected={patientsReadyToMerge.includes(patient)}
              />
            ))}
          </div>
        </div>
      : <EmptyState showNoSearchResults={showNoSearchResults} showNoRemainingResults={showNoRemainingResults} />}
    </div>
  )
}
